import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import moment from "moment";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { ActionsSubject } from "@ngrx/store";
import { ofType } from "@ngrx/effects";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import { StoreExtendedService } from "../../shared/store-extended.service";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "../../auth/auth.service";
import { User } from "../../users/users.interface";
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";
import { SyncRequiredDialogComponent } from "../../shared/sync-required-dialog/sync-required-dialog.component";
import { SyncRequiredDialogData } from "../../shared/sync-required-dialog/sync-required-dialog.interface";
import { isFeatureBlocked } from "../../shared/utils/plan-restriction.util";
import { SyncSuccessDialogComponent } from "../sync-success-dialog/sync-success-dialog.component";
import { NotifyAdminDialogComponent } from "../../shared/notify-admin-dialog/notify-admin-dialog.component";
import { MY_FORMATS } from "../../shared/shared.module";
import {
  GetIntegrationSettingsAction,
  ChangeIntegrationSettingsAction,
  CHANGE_INTEGRATION_SETTINGS_SUCCESS,
  CHANGE_INTEGRATION_SETTINGS_FAILURE,
} from "../../settings/settings.actions";
import { IntegrationSettings } from "../../settings/settings.interface";
import { getSettingsSelector } from "../../settings/settings.reducer";
import { IntegrationSettingsService } from "../integration-settings.service";
import { BillingSettings } from "../../settings/settings.interface";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import { environment } from "../../../environments/environment";
import { getCurrentAccount } from "../../accounts/accounts.reducer";
import {
  SyncGoogleEmployeesAction,
  SYNC_GOOGLE_EMPLOYEES_SUCCESS,
  SYNC_GOOGLE_EMPLOYEES_FAILURE,
} from "../../employees/employees.actions";
import { EmployeesService } from "../../employees/employees.service";

initializeApp(environment.firebaseConfig);

@Component({
  selector: "app-google-integration",
  templateUrl: "./google-integration.component.html",
  styleUrls: ["./google-integration.component.scss"],
  standalone: false,
})
export class GoogleIntegrationComponent
  implements OnInit, OnDestroy, OnChanges
{
  @ViewChild("proxyToggle") proxyToggle!: MatSlideToggle;
  @Input() googleExpanded = true;
  @Input() otherAbsenceEnabled = false;
  @Input() billingSettings: BillingSettings | null = null;
  @Input() activeSyncs: {
    googleSyncStateId?: string;
    microsoftSyncStateId?: string;
    calamariSyncStateId?: string;
  } = {};
  @Output() googleExpandedChange = new EventEmitter<boolean>();
  @Output() absenceEnabledChange = new EventEmitter<boolean>();

  public environment = environment;
  public currentDomain: string | null = null;
  public hasChanges = false;
  private initialSettings: {
    proxy: { active: boolean; activeAll: boolean; emails: string[] } | null;
    internalSignature: boolean;
    absenceEnabled: boolean;
    absenceLabel: string;
    absenceDaysAhead: number;
  } | null = null;

  public isSuperAdmin: boolean;
  public appUser: User | null | undefined = null;

  public readonly clientId = "111875350985434773237";
  public readonly scopes =
    "https://www.googleapis.com/auth/gmail.settings.sharing, https://www.googleapis.com/auth/gmail.settings.basic, https://www.googleapis.com/auth/admin.directory.user.readonly, https://www.googleapis.com/auth/admin.directory.orgunit.readonly, https://www.googleapis.com/auth/admin.directory.group.readonly, https://www.googleapis.com/auth/calendar.readonly, https://www.googleapis.com/auth/calendar.events";
  public readonly redirectUri =
    "https://gstamp.firebaseapp.com/__/auth/handler";

  public lastGoogleSync: Date | undefined;
  public lastGoogleSyncFormatted: string | undefined;

  public proxyEnabled = false;
  public internalSignatureEnabled = false;
  public absenceEnabled = false;
  public absenceLabel = "";
  public absenceDaysAhead = 30;
  private currentSettings: IntegrationSettings | null = null;

  public savingProxy = false;
  private destroy$ = new Subject<void>();

  public readonly proxyDNS = "34.141.31.195";
  public readonly proxyHostIp = "35.242.206.24";
  public readonly proxyPort = "587";
  public readonly xGSignature = "X-gSignature-MessageProcessed: Yes";
  public readonly fromSenderRegex =
    "(From:.*|Sender:.*)?(drive-shares-dm-noreply@google.com|drive-shares-noreply@google.com|calendar-notification@google.com)";
  public readonly proxySPF = "v=spf1 include:_spf.google.com ~all";
  public generatedRegex = "";
  public routingEmails = "";
  public routingAllEmails = true;

  public step1Completed = false;
  public step2Completed = false;
  public step3Completed = false;
  public step4Completed = false;
  public spfCheckResult: boolean | null = null;

  public googleSyncStateId: string | null = null;
  public showGoogleProgress = false;

  public isGoogleSyncInProgress = false;

  openGoogleWorkspaceMarketplace(): void {
    window.open(
      "https://workspace.google.com/marketplace/app/gsignature/808552810822",
      "_blank",
    );
  }

  openNotifyAdminDialog(): void {
    this.dialog.open(NotifyAdminDialogComponent, {
      width: "700px",
      maxWidth: "90vw",
      data: { provider: "google" },
    });
  }

  constructor(
    private readonly toastr: ToastrTranslatedService,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService,
    private readonly auth: AuthService,
    private readonly store: StoreExtendedService,
    private readonly actions$: ActionsSubject,
    private readonly integrationSettingsService: IntegrationSettingsService,
    private readonly http: HttpClient,
    private readonly employeesService: EmployeesService,
    private readonly dialog: MatDialog,
  ) {
    this.lastGoogleSync = this.auth.appUser.lastGoogleSync;
    if (this.lastGoogleSync) {
      this.lastGoogleSyncFormatted = moment(this.lastGoogleSync).format(
        MY_FORMATS.display.dateInput,
      );
    }
    this.isSuperAdmin = this.auth.isSuperAdmin;

    this.actions$
      .pipe(
        ofType(
          CHANGE_INTEGRATION_SETTINGS_SUCCESS,
          CHANGE_INTEGRATION_SETTINGS_FAILURE,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.savingProxy = false;
      });
  }

  ngOnInit(): void {
    this.appUser = this.auth.appUser;

    this.store.select(getCurrentAccount).subscribe((domain) => {
      this.currentDomain = domain;
      this.generateRegex();
    });

    if (this.activeSyncs.googleSyncStateId) {
      this.googleSyncStateId = this.activeSyncs.googleSyncStateId;
      this.showGoogleProgress = true;
      this.isGoogleSyncInProgress = true;
    }

    this.store.dispatch(new GetIntegrationSettingsAction());
    this.store
      .select(getSettingsSelector)
      .subscribe((settings: IntegrationSettings | null) => {
        if (!settings) {
          return;
        }
        this.currentSettings = settings;
        this.proxyEnabled = settings.proxy?.active;
        this.routingAllEmails = settings.proxy?.activeAll ?? true;
        this.routingEmails = settings.proxy?.emails?.join(", ") ?? "";
        this.internalSignatureEnabled = settings.internalSignature ?? false;
        this.absenceEnabled = settings.absenceEnabled ?? false;
        this.absenceLabel = settings.absenceLabel ?? "";
        this.absenceDaysAhead = settings.absenceDaysAhead ?? 30;

        this.initialSettings = {
          proxy: settings.proxy
            ? {
                active: settings.proxy.active,
                activeAll: settings.proxy.activeAll,
                emails: settings.proxy.emails,
              }
            : null,
          internalSignature: this.internalSignatureEnabled,
          absenceEnabled: this.absenceEnabled,
          absenceLabel: this.absenceLabel,
          absenceDaysAhead: this.absenceDaysAhead,
        };

        if (this.proxyEnabled) {
          this.step1Completed = true;
          this.step2Completed = true;
          this.step3Completed = true;
          this.step4Completed = true;
          this.spfCheckResult = true;
        }
      });

    const auth = getAuth();
    void getRedirectResult(auth).then((result) => {
      if (result && result.user) {
        GoogleAuthProvider.credentialFromResult(result);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleProxy(checked: boolean): void {
    if (checked && isFeatureBlocked(this.isSuperAdmin, this.billingSettings)) {
      this.proxyToggle.checked = false;
      const planName = this.billingSettings?.planName?.toLowerCase() || "basic";
      this.showFreemiumRestrictionDialog(
        planName as "free" | "freemium" | "basic",
      );
      return;
    }

    if (checked && !this.lastGoogleSync) {
      this.proxyToggle.checked = false;
      this.showSyncRequiredDialog("GOOGLE", () => this.syncGoogle());
      return;
    }

    this.proxyEnabled = checked;
    this.checkForChanges();
    if (checked) {
      this.step1Completed = false;
      this.step2Completed = false;
      this.step3Completed = false;
      this.step4Completed = false;
      this.spfCheckResult = null;
    } else {
      this.internalSignatureEnabled = false;
    }
  }

  showFreemiumRestrictionDialog = (plan: "free" | "freemium" | "basic") => {
    this.dialog.open(LimitPlanDialogComponent, {
      width: "500px",
      maxWidth: "90vw",
      data: { plan, type: "general" },
    });
  };

  showSyncRequiredDialog = (
    provider: "GOOGLE" | "MICROSOFT" | "CALAMARI",
    onSync: () => void,
  ) => {
    const data: SyncRequiredDialogData = {
      provider,
      onSync,
    };

    this.dialog.open(SyncRequiredDialogComponent, {
      width: "500px",
      maxWidth: "90vw",
      data,
    });
  };

  toggleInternalSignature(checked: boolean): void {
    this.internalSignatureEnabled = checked;
    this.checkForChanges();
  }

  onAbsenceLabelChange(): void {
    this.checkForChanges();
  }

  onAbsenceDaysAheadChange(): void {
    this.checkForChanges();
  }

  private checkForChanges(): void {
    if (!this.initialSettings) {
      return;
    }
    const currentProxy = this.proxyEnabled
      ? {
          active: this.proxyEnabled,
          activeAll: this.routingAllEmails,
          emails: this.routingEmails
            .split(",")
            .map((e) => e.trim())
            .filter((e) => e.length > 0),
        }
      : {
          active: false,
          activeAll: false,
          emails: [],
        };
    const proxyChanged =
      JSON.stringify(currentProxy) !==
      JSON.stringify(this.initialSettings.proxy);

    this.hasChanges =
      proxyChanged ||
      this.internalSignatureEnabled !==
        this.initialSettings.internalSignature ||
      this.absenceEnabled !== this.initialSettings.absenceEnabled ||
      this.absenceLabel !== this.initialSettings.absenceLabel ||
      this.absenceDaysAhead !== this.initialSettings.absenceDaysAhead;
  }

  copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text);
    this.toastr.success("settings.integration.copied");
  };

  public onStepChange(): void {
    this.hasChanges = true;
  }

  public get backendProxyEnabled(): boolean {
    return this.currentSettings ? this.currentSettings.proxy?.active : false;
  }

  public verifySpfRecord(): void {
    this.spfCheckResult = null;
    this.integrationSettingsService.checkSpfRecord().subscribe({
      next: (res) => {
        this.spfCheckResult = res.valid;
        this.step4Completed = res.valid;
        if (res.valid) {
          this.toastr.success(
            this.translate.instant(
              "settings.integration.google.serverSide.spfValid",
            ) as string,
          );
        } else {
          this.toastr.error(
            this.translate.instant(
              "settings.integration.google.serverSide.spfInvalid",
            ) as string,
          );
        }
      },
      error: () => {
        this.spfCheckResult = false;
        this.step4Completed = false;
        this.toastr.error(
          this.translate.instant(
            "settings.integration.google.serverSide.spfError",
          ) as string,
        );
      },
    });
  }

  get isAllStepsCompleted(): boolean {
    return (
      !this.proxyEnabled ||
      (this.step1Completed &&
        this.step2Completed &&
        this.step3Completed &&
        this.step4Completed)
    );
  }

  get canSaveServerSide(): boolean {
    if (this.savingProxy || !this.hasChanges) {
      return false;
    }
    if (!this.proxyEnabled) {
      return true;
    }
    if (this.isAllStepsCompleted) {
      return true;
    }
    return this.initialSettings?.proxy?.active;
  }

  public saveIntegrationSettings(): void {
    if (!this.currentSettings) {
      this.toastr.error(
        "settings.integration.google.serverSide.currentSettings",
      );
      return;
    }
    this.savingProxy = true;
    const updatedSettings: IntegrationSettings = {
      ...this.currentSettings,
      proxy: this.proxyEnabled
        ? {
            active: this.proxyEnabled,
            activeAll: this.routingAllEmails,
            emails: this.routingEmails
              .split(",")
              .map((e) => e.trim())
              .filter((e) => e.length > 0),
          }
        : {
            active: false,
            activeAll: false,
            emails: [],
          },
      internalSignature: this.internalSignatureEnabled,
      absenceEnabled: this.absenceEnabled,
      absenceLabel: this.absenceLabel || null,
      absenceDaysAhead: this.absenceDaysAhead,
    };
    this.store
      .dispatchAndWaitForResult(
        new ChangeIntegrationSettingsAction(updatedSettings),
      )
      .subscribe(() => {
        this.store.dispatch(new GetIntegrationSettingsAction());
      });
    this.hasChanges = false;
    this.initialSettings = {
      proxy: updatedSettings.proxy
        ? {
            active: updatedSettings.proxy.active,
            activeAll: updatedSettings.proxy.activeAll,
            emails: updatedSettings.proxy.emails,
          }
        : null,
      internalSignature: this.internalSignatureEnabled,
      absenceEnabled: this.absenceEnabled,
      absenceLabel: this.absenceLabel?.trim() || "",
      absenceDaysAhead: this.absenceDaysAhead,
    };
  }

  public authorizeFirebase(): void {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    this.scopes
      .split(",")
      .map((scope) => scope.trim())
      .forEach((scope) => {
        provider.addScope(scope);
      });
    void signInWithRedirect(auth, provider);
  }

  public generateRegex(): void {
    if (this.routingAllEmails) {
      if (!this.currentDomain) {
        this.generatedRegex = "";
        return;
      }
      const escaped = this.currentDomain.replace(/\./g, "\\.");
      this.generatedRegex = `(?i)^[A-Za-z0-9._%+-]+@${escaped}$`;
    } else {
      const emails = this.routingEmails
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email.length > 0)
        .map((email) => email.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      if (emails.length > 0) {
        this.generatedRegex = `(?i)^(${emails.join("|")})$`;
      } else {
        this.generatedRegex = "";
      }
    }
  }

  public onRoutingEmailsChange(): void {
    this.generateRegex();
    this.checkForChanges();
  }

  public onRoutingAllEmailsChange(): void {
    this.generateRegex();
    this.checkForChanges();
  }

  syncGoogle() {
    if (this.isGoogleSyncInProgress) {
      return;
    }
    this.isGoogleSyncInProgress = true;
    this.showGoogleProgress = true;
    this.googleSyncStateId = null;
    this.employeesService.checkSyncLock().subscribe(
      (response) => {
        if (response.lock) {
          this.isGoogleSyncInProgress = false;
          this.showGoogleProgress = false;
          const message = this.translate.instant(
            "employee.syncAlreadyInProgress",
            {
              currentSyncUser: response.currentSyncUser,
            },
          ) as string;
          this.toastrService.info(message);
        } else {
          this.startGoogleSync();
        }
      },
      () => {
        this.isGoogleSyncInProgress = false;
        this.showGoogleProgress = false;
        this.toastr.error(
          this.translate.instant("employee.syncLockCheckError") as string,
        );
      },
    );
  }

  private startGoogleSync(): void {
    this.store.dispatch(new SyncGoogleEmployeesAction());

    this.actions$
      .pipe(
        ofType(SYNC_GOOGLE_EMPLOYEES_SUCCESS, SYNC_GOOGLE_EMPLOYEES_FAILURE),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((action: { payload?: { googleSyncStateId?: string } }) => {
        if (action.payload?.googleSyncStateId) {
          this.googleSyncStateId = action.payload.googleSyncStateId;
        }
      });
  }

  closeGoogleProgress(): void {
    this.isGoogleSyncInProgress = false;
    this.showGoogleProgress = false;
    this.googleSyncStateId = null;
  }

  onGoogleSyncCompleted(event: { success: boolean }): void {
    this.isGoogleSyncInProgress = false;
    if (!event.success) {
      return;
    }
    this.updateLastGoogleSync();
    void this.auth.fetchCurrent().then(() => {
      const synced = this.auth.appUser?.lastGoogleSync;
      if (!synced) {
        return;
      }
      this.lastGoogleSync = new Date(synced);
      this.lastGoogleSyncFormatted = moment(this.lastGoogleSync).format(
        MY_FORMATS.display.dateInput,
      );
      this.auth.lastGoogleSync = this.lastGoogleSync;
    });
    this.showSyncSuccessDialog();
  }

  private updateLastGoogleSync() {
    this.lastGoogleSync = new Date();
    this.lastGoogleSyncFormatted = moment(this.lastGoogleSync).format(
      MY_FORMATS.display.dateInput,
    );
    if (this.auth.appUser) {
      this.auth.appUser.lastGoogleSync = this.lastGoogleSync;
    }
    this.auth.lastGoogleSync = this.lastGoogleSync;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeSyncs?.currentValue) {
      const activeSyncs = changes.activeSyncs.currentValue as {
        googleSyncStateId?: string;
      };
      if (activeSyncs.googleSyncStateId) {
        this.googleSyncStateId = activeSyncs.googleSyncStateId;
        this.showGoogleProgress = true;
        this.isGoogleSyncInProgress = true;
      }
    }
  }

  toggleAbsence(checked: boolean): void {
    if (checked && this.otherAbsenceEnabled) {
      return;
    }

    if (checked && !this.lastGoogleSync) {
      this.showSyncRequiredDialog("GOOGLE", () => this.syncGoogle());
      return;
    }

    this.absenceEnabled = checked;
    this.absenceEnabledChange.emit(this.absenceEnabled);
    this.checkForChanges();
  }

  private showSyncSuccessDialog(): void {
    this.dialog.open(SyncSuccessDialogComponent, {
      width: "500px",
      disableClose: true,
      autoFocus: false,
      panelClass: "app-dialog-panel",
    });
  }
}
